/**
 * Shared vocabulary for the three Agent action variants — Native, Foundry,
 * and Coding. Centralizes the badge token pairs, glyph mapping, type-guard
 * composition, and the `defaultInputs` shape applied when a user picks a
 * variant. All UI surfaces (picker, command bar, canvas, node editor,
 * dotted edges) should derive their per-variant colors, icons, and inputs
 * from this module so the three variants stay visually + behaviourally in
 * lockstep.
 */

import { makeStyles } from '@coreai/fluentui-react';
import { BotFilled, BotSparkleFilled } from '@fluentui/react-icons';
import { CodingAgentGlyph } from '@/components/icons/CodingAgentGlyph';
import { ottoTokens } from '@/theme/ottoTokens';
import type { AgentModelType } from '@/types/agentConnection';
import type { OperationDefinition } from '@/types/operations';
import type { Action } from '@/types/workflowDefinition';
import {
  isAgentAction,
  isCodingAgentAction,
  isCodingAgentHarnessType,
  isFoundryAgentAction,
  isFoundryAgentModelType,
  isHostedAgentAction,
} from '@/types/workflowDefinition';
import { MANAGED_MODEL_PROVIDER_ID, MANAGED_MODEL_REFERENCE_KEY, type AgentModelProvider } from '@/utils/connections';

/** Visual + behavioural variant of the Agent action. */
export type AgentVariant = 'native' | 'foundry' | 'managed' | 'hosted';

/** All variants in the canonical UI ordering. */
export const AGENT_VARIANTS: readonly AgentVariant[] = ['native', 'foundry', 'managed', 'hosted'];

export interface AgentBadgeTokens {
  background: string;
  foreground: string;
}

const AGENT_BADGE_TOKENS: Record<AgentVariant, AgentBadgeTokens> = {
  native: {
    background: ottoTokens.agentNativeBadgeBackground,
    foreground: ottoTokens.agentNativeBadgeForeground,
  },
  foundry: {
    background: ottoTokens.agentFoundryBadgeBackground,
    foreground: ottoTokens.agentFoundryBadgeForeground,
  },
  managed: {
    background: ottoTokens.agentCodingBadgeBackground,
    foreground: ottoTokens.agentCodingBadgeForeground,
  },
  hosted: {
    background: ottoTokens.agentHostedBadgeBackground,
    foreground: ottoTokens.agentHostedBadgeForeground,
  },
};

/** Background + foreground token pair for a given agent variant. */
export const getAgentBadgeTokens = (variant: AgentVariant): AgentBadgeTokens =>
  AGENT_BADGE_TOKENS[variant];

const AGENT_BRAND_COLORS: Record<AgentVariant, string> = {
  native: ottoTokens.agentNativeBrand,
  foundry: ottoTokens.agentFoundryBrand,
  managed: ottoTokens.agentCodingBrand,
  hosted: ottoTokens.agentHostedBrand,
};

/** Saturated brand identity color for a given agent variant. */
export const getAgentBrandColor = (variant: AgentVariant): string =>
  AGENT_BRAND_COLORS[variant];

// Underscore-separated so they survive `.replace(/\s+/g, '_')` in callers.
// Mirrors the variant labels in AgentTypeView (Workflow / Foundry prompt / Managed).
const AGENT_VARIANT_ACTION_NAMES: Record<AgentVariant, string> = {
  native: 'Workflow_Agent',
  foundry: 'Foundry_Agent',
  managed: 'Managed_Agent',
  hosted: 'Hosted_Agent',
};

/** Default base action-name seed for a given agent variant. */
export const getAgentVariantActionName = (variant: AgentVariant): string =>
  AGENT_VARIANT_ACTION_NAMES[variant];

// Human-readable eyebrow/tooltip labels for each agent variant. Kept short so
// they fit the 12px eyebrow slot in the node editor header without wrapping.
const AGENT_VARIANT_LABELS: Record<AgentVariant, string> = {
  native: 'AI Agent',
  foundry: 'Foundry Agent',
  managed: 'Managed Agent',
  hosted: 'Hosted Agent',
};

/** Short display label for a given agent variant (e.g. "Foundry Agent"). */
export const getAgentVariantLabel = (variant: AgentVariant): string =>
  AGENT_VARIANT_LABELS[variant];

export interface AgentOperationSelection {
  variant: AgentVariant;
  /** Connection reference key to pre-populate for BYOM; Microsoft-managed uses it only in the editor. */
  connectionRefName?: string;
  /**
   * Model provider backing the connection. Used by the native variant to
   * switch `agentModelType` between BYOM (`'AzureOpenAI'`) and
   * Microsoft-managed (`'MicrosoftManaged'`). Coding, Foundry, and
   * hosted variants ignore this — Managed Agent doesn't support
   * Microsoft-managed yet, and Foundry/hosted `agentModelType` is fixed by
   * `applyAgentVariantDefaults`.
   */
  modelProvider?: AgentModelProvider;
  /** Curated deployment to preselect for a new Microsoft-managed Workflow Agent. */
  defaultManagedModelId?: string;
}

/**
 * Builds the runtime `OperationDefinition` for an agent variant from the
 * shared `AGENT_OPERATIONS[0]` base — applies the variant action name,
 * variant defaults, and optional connection pre-fill in one place so every
 * picker call site stays in lockstep.
 */
export const buildAgentOperationFromSelection = (
  baseOp: OperationDefinition,
  selection: AgentOperationSelection,
): OperationDefinition => {
  const { variant, connectionRefName, modelProvider, defaultManagedModelId } = selection;
  const baseInputs = (baseOp.defaultInputs ?? {}) as Record<string, unknown>;
  const managedModelSelected = variant === 'native'
    && (modelProvider === MANAGED_MODEL_PROVIDER_ID || connectionRefName === MANAGED_MODEL_REFERENCE_KEY);
  const resolvedProvider = managedModelSelected ? MANAGED_MODEL_PROVIDER_ID : modelProvider;
  const withVariant = applyAgentVariantDefaults(
    baseInputs,
    variant,
    resolvedProvider !== undefined ? { modelProvider: resolvedProvider } : {},
  );
  const withModel = managedModelSelected && defaultManagedModelId
    ? {
        ...withVariant,
        parameters: {
          ...((withVariant.parameters as Record<string, unknown> | undefined) ?? {}),
          deploymentId: defaultManagedModelId,
        },
      }
    : withVariant;
  const defaultInputs: Record<string, unknown> = connectionRefName && !managedModelSelected
    ? {
        ...withModel,
        modelConfigurations: {
          ...((withModel.modelConfigurations as Record<string, unknown> | undefined) ?? {}),
          model1: { referenceName: connectionRefName },
        },
      }
    : withModel;
  return {
    ...baseOp,
    name: getAgentVariantActionName(variant),
    defaultInputs,
  };
};

/** Resolves the variant of an Action — `null` for non-agent actions. */
export const getAgentVariant = (action: Action): AgentVariant | null => {
  if (!isAgentAction(action)) return null;
  if (isHostedAgentAction(action)) return 'hosted';
  if (isCodingAgentAction(action)) return 'managed';
  if (isFoundryAgentAction(action)) return 'foundry';
  return 'native';
};

/**
 * Same as `getAgentVariant` but for components that only have the raw
 * `agentModelType` / `agentHarnessType` strings — e.g. canvas nodes that
 * read from flattened `data`, or sub-workflow previews assembling fresh
 * graph nodes.
 */
export const getAgentVariantFromTypes = (
  modelType: string | undefined,
  harnessType: string | undefined,
): AgentVariant => {
  if (modelType === 'FoundryHostedAgent') return 'hosted';
  if (isCodingAgentHarnessType(harnessType)) return 'managed';
  if (isFoundryAgentModelType(modelType)) return 'foundry';
  return 'native';
};

/**
 * Model type to use when creating a new connection for an agent. Legacy
 * Foundry nodes remain usable, but all newly-created Foundry connections use
 * the V2 Responses API contract.
 */
export const getAgentConnectionCreationModelType = (
  modelType: string | undefined,
  variant: AgentVariant,
): AgentModelType | undefined => {
  if (variant === 'foundry') return 'FoundryAgentServiceV2';

  switch (modelType) {
    case 'AzureOpenAI':
    case 'FoundryAgentService':
    case 'FoundryAgentServiceV2':
    case 'APIMGenAIGateway':
    case 'V1ChatCompletionsService':
    case 'GitHubCopilot':
      return modelType;
    default:
      return undefined;
  }
};

/**
 * Merges variant-specific defaults into a base Agent operation's
 * `defaultInputs`:
 *   - native  → unchanged (unless Microsoft-managed — see `options.modelProvider`)
 *   - foundry → `agentModelType: 'FoundryAgentServiceV2'`
 *   - managed → `agentModelType: 'AzureOpenAI'` + nested
 *               `parameters.agentModelSettings.agentHarness.type: 'GHCP'`
 *   - hosted  → `agentModelType: 'FoundryHostedAgent'`
 *
 * When `options.modelProvider === 'MicrosoftManaged'` for the native variant,
 * `agentModelType` is stamped `'MicrosoftManaged'` instead — signaling the
 * runtime to resolve the endpoint + credentials from the Microsoft-managed env-var pool
 * rather than from the connection blob. Managed Agent doesn't support
 * Microsoft-managed yet (BYOM-only until the managed runtime path supports
 * the GHCP harness), so the hint is intentionally ignored for `managed` —
 * its `agentModelType` always stays `'AzureOpenAI'`. Foundry and hosted
 * variants likewise ignore the provider hint — their `agentModelType` is
 * fixed by the variant.
 */
export const applyAgentVariantDefaults = (
  baseInputs: Record<string, unknown>,
  variant: AgentVariant,
  options?: { modelProvider?: AgentModelProvider },
): Record<string, unknown> => {
  if (variant === 'hosted') {
    return { ...baseInputs, agentModelType: 'FoundryHostedAgent' };
  }
  if (variant === 'foundry') {
    return { ...baseInputs, agentModelType: 'FoundryAgentServiceV2' };
  }
  if (variant === 'managed') {
    const params = (baseInputs.parameters as Record<string, unknown> | undefined) ?? {};
    const settings = (params.agentModelSettings as Record<string, unknown> | undefined) ?? {};
    return {
      ...baseInputs,
      agentModelType: 'AzureOpenAI',
      parameters: {
        ...params,
        agentModelSettings: {
          ...settings,
          agentHarness: { type: 'GHCP' },
        },
      },
    };
  }
  // native
  if (options?.modelProvider === MANAGED_MODEL_PROVIDER_ID) {
    return { ...baseInputs, agentModelType: MANAGED_MODEL_PROVIDER_ID };
  }
  return baseInputs;
};

const useAgentBadgeClasses = makeStyles({
  native: {
    backgroundColor: ottoTokens.agentNativeBadgeBackground,
    color: ottoTokens.agentNativeBadgeForeground,
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
      color: 'HighlightText',
    },
  },
  foundry: {
    backgroundColor: ottoTokens.agentFoundryBadgeBackground,
    color: ottoTokens.agentFoundryBadgeForeground,
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
      color: 'HighlightText',
    },
  },
  managed: {
    backgroundColor: ottoTokens.agentCodingBadgeBackground,
    color: ottoTokens.agentCodingBadgeForeground,
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
      color: 'HighlightText',
    },
  },
  hosted: {
    backgroundColor: ottoTokens.agentHostedBadgeBackground,
    color: ottoTokens.agentHostedBadgeForeground,
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
      color: 'HighlightText',
    },
  },
});

/**
 * Returns a `(variant) => className` lookup for the pastel badge
 * background + foreground pair, with a `forced-colors` fallback.
 */
export const useAgentBadgeClass = (): ((variant: AgentVariant) => string) => {
  const classes = useAgentBadgeClasses();
  return (variant) => classes[variant];
};

const useAgentIconForegroundClasses = makeStyles({
  native: {
    color: ottoTokens.agentNativeBadgeForeground,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
  foundry: {
    color: ottoTokens.agentFoundryBadgeForeground,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
  managed: {
    color: ottoTokens.agentCodingBadgeForeground,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
  hosted: {
    color: ottoTokens.agentHostedBadgeForeground,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
});

/**
 * Returns a `(variant) => className` lookup for the variant glyph color
 * (foreground only), with a `forced-colors` fallback. Use when the
 * surrounding container already paints its own background (e.g. the
 * brand-color tile in the node editor header).
 */
export const useAgentIconForegroundClass = (): ((variant: AgentVariant) => string) => {
  const classes = useAgentIconForegroundClasses();
  return (variant) => classes[variant];
};

/** Glyph for the given agent variant. */
export const AgentVariantIcon = ({ variant }: { variant: AgentVariant }) => {
  if (variant === 'managed') return <CodingAgentGlyph />;
  if (variant === 'foundry') return <BotSparkleFilled />;
  if (variant === 'hosted') return <BotSparkleFilled />;
  return <BotFilled />;
};
